import React, { useState, useEffect } from 'react';
import { CopyIcon, CheckIcon, UsersIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { authService } from '../services';
import { formatPrice } from '../utils/priceFormat';

// Component props definition
interface ReferralCodeCardProps {
  className?: string;
}

// Data structure for referral information
interface ReferralData {
  code?: string;
  has_code: boolean;
  credit_balance: number;
  referrals_count: number;
  credit_amount: number;
  discount_amount: number;
  short_url?: string;
}

export const ReferralCodeCard: React.FC<ReferralCodeCardProps> = ({ className }) => {
  // State declarations
  const { t } = useTranslation();
  const [referralData, setReferralData] = useState<ReferralData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [codeNotFound, setCodeNotFound] = useState(false);
  const [creatingCode, setCreatingCode] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [shortUrl, setShortUrl] = useState('');
  const [creditBalance, setCreditBalance] = useState(0);
  const [referralsCount, setReferralsCount] = useState(0);

  // Fetch data on component mount
  useEffect(() => {
    fetchReferralCode();
  }, []);

  const fetchReferralCode = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = authService.getToken();
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      const response = await fetch('/api/referrals/code', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const data = await response.json() as any;
        throw new Error(data.error || 'Failed to fetch referral code');
      }
      
      const data = await response.json() as ReferralData;
      
      if (!data.has_code) {
        console.log('Referral code not found, showing create option');
        setCodeNotFound(true);
        setLoading(false);
        return;
      }

      setReferralData(data);
      setReferralCode(data.code || '');
      setShortUrl(data.short_url || '');
      setCreditBalance(data.credit_balance || 0);
      setReferralsCount(data.referrals_count || 0);
      setCodeNotFound(false);
    } catch (err) {
      console.error('Error fetching referral code:', err);
      setError('Failed to load referral code');
    } finally {
      setLoading(false);
    }
  };

  const createReferralCode = async () => {
    setCreatingCode(true);
    setError(null);
    try {
      const token = authService.getToken();
      if (!token) {
        setError('Not authenticated');
        setCreatingCode(false);
        return;
      }

      const response = await fetch('/api/referrals/create-code', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          create_promo_code: false // Create only internal referral code, not Stripe promo code
        })
      });

      const data = await response.json() as any;
      
      if (!response.ok) {
        console.error('Error creating referral code:', data);
        throw new Error(data.error || 'Failed to create referral code');
      }

      const typedData = data as ReferralData;
      setReferralData({
        ...typedData,
        has_code: true
      });
      setReferralCode(typedData.code || '');
      setShortUrl(typedData.short_url || '');
      setCreditBalance(typedData.credit_balance || 0);
      setReferralsCount(typedData.referrals_count || 0);
      setCodeNotFound(false);
      toast.success(t('account.referral.createdSuccessfully'));
    } catch (err) {
      console.error('Error creating referral code:', err);
      let errorMessage = 'Failed to create referral code. Please try again.';
      
      if (err instanceof Error) {
        // Check for specific error messages from the backend
        if (err.message.includes('User not found in authentication system')) {
          errorMessage = 'Your account needs to be fully set up before creating a referral code.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setCreatingCode(false);
    }
  };

  const copyToClipboard = async () => {
    if (!referralData) return;
    
    const textToCopy = referralData.short_url || referralData.code || '';
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      toast.success(t('account.referral.copiedToClipboard'));
      
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error(t('account.referral.failedToCopy'));
    }
  };
  
  const shareReferral = () => {
    if (!referralData) return;
    
    const shareUrl = referralData.short_url || `https://piika.co/flowers?promo=${referralData.code}`;
    const shareText = `Join me on Piika! ${referralData.short_url ? 'Use my referral link' : `Use my referral code ${referralData.code}`} to get ฿${referralData.discount_amount} off your first subscription. ${shareUrl}`;
    
    if (navigator.share) {
      navigator.share({
        title: t('account.referral.shareTitle'),
        text: shareText,
        url: shareUrl
      }).catch(err => {
        console.error('Error sharing:', err);
      });
    } else {
      copyToClipboard();
      toast.success(t('account.referral.shareLinkCopied'));
    }
  };
  
  if (loading) {
    return (
      <div className={`bg-white p-6 rounded-xl shadow-sm animate-pulse ${className}`}>
        <div className="h-7 w-2/3 bg-gray-200 rounded mb-4"></div>
        <div className="h-12 bg-gray-200 rounded-xl mb-3"></div>
        <div className="h-12 bg-gray-200 rounded-xl mb-5"></div>
        
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div className="h-20 bg-gray-200 rounded-xl"></div>
          <div className="h-20 bg-gray-200 rounded-xl"></div>
        </div>
        
        <div className="h-4 bg-gray-200 rounded mb-2"></div>
        <div className="h-4 bg-gray-200 rounded mb-5"></div>
        <div className="h-12 bg-gray-200 rounded-xl"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={`bg-white p-6 rounded-xl shadow-sm ${className}`}>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {shortUrl ? t('account.referral.yourReferralLink') : t('account.referral.yourReferralCode')}
        </h3>
        <p className="text-red-500 mb-4">{error}</p>
        <button 
          className="text-blue-600 hover:text-blue-800"
          onClick={fetchReferralCode}
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  if (codeNotFound || !referralData?.has_code) {
    return (
      <div className={`bg-white p-6 rounded-xl shadow-sm ${className}`}>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('account.referral.noCodeFound')}</h3>
        <p className="text-sm text-gray-600 mb-4">{t('account.referral.getPersonalCode')}</p>
        <button 
          onClick={createReferralCode}
          disabled={creatingCode} 
          className="w-full text-center py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center justify-center"
        >
          {creatingCode ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('account.referral.creating')}
            </span>
          ) : (
            <>
              <UsersIcon className="h-4 w-4 mr-2" />
              {t('account.referral.createCode')}
            </>
          )}
        </button>
      </div>
    );
  }
  
  return (
    <div className={`bg-white p-6 rounded-xl shadow-sm ${className}`}>
      <h3 className="text-xl font-semibold text-gray-900 mb-4">
        {referralData?.short_url ? t('account.referral.yourReferralLink') : t('account.referral.yourReferralCode')}
      </h3>
      
      <div className="bg-gray-50 py-3 px-4 rounded-xl border border-gray-200 font-mono text-base overflow-x-auto w-full relative mb-3">
        {referralData?.short_url || referralData?.code}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-300 rounded-b-xl opacity-80"></div>
      </div>
      
      <button 
        className="w-full bg-white text-blue-600 font-medium py-3 px-4 rounded-xl border border-gray-200 shadow-sm transition-colors text-lg mb-5"
        onClick={copyToClipboard}
        aria-label={referralData?.short_url ? t('account.referral.copyReferralLinkAria') : t('account.referral.copyReferralCodeAria')}
      >
        {copied ? t('account.referral.copied') : t('account.referral.copy')}
      </button>
      
      <div className="mb-5 grid grid-cols-2 gap-4">
        <div className="text-center p-4 bg-blue-50 rounded-xl">
          <div className="text-gray-500 text-sm mb-1">{t('account.referralCard.creditBalance')}</div>
                      <div className="text-3xl font-semibold">฿{formatPrice(referralData?.credit_balance || 0, 'thb')}</div>
        </div>
        <div className="text-center p-4 bg-blue-50 rounded-xl">
          <div className="text-gray-500 text-sm mb-1">{t('account.referralCard.referralsCountLabel')}</div>
          <div className="text-3xl font-semibold">{referralData?.referrals_count}</div>
        </div>
      </div>
      
      <p className="text-sm text-gray-600 mb-5">
        Share your {referralData?.short_url ? 'link' : 'code'} with friends and earn ฿{referralData?.credit_amount} when they subscribe! 
        They'll get ฿{referralData?.discount_amount} off their first subscription.
      </p>
      
      <button 
        className="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-xl flex items-center justify-center gap-3 transition-colors font-medium"
        onClick={shareReferral}
      >
        <UsersIcon size={20} />
        <span>{t('account.referralCard.shareWithFriends')}</span>
      </button>
    </div>
  );
}; 